import { useQuery } from '@tanstack/react-query';
import { isTerminalVerdict, type SubmissionDetail } from '@flarecode/shared';
import { api } from '../lib/api';

/**
 * Fetch a submission and poll every 500ms while its verdict is non-terminal
 * (pending or running). Once the verdict is terminal, polling stops.
 */
export function useSubmission(id: string | undefined): {
  submission: SubmissionDetail | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const query = useQuery<SubmissionDetail>({
    queryKey: ['submission', id],
    queryFn: () => api.getSubmission(id as string),
    enabled: !!id,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return 500; // still loading initial data
      return isTerminalVerdict(data.verdict) ? false : 500;
    },
    refetchIntervalInBackground: false,
    retry: (count, err) => {
      // Don't retry 404 — the submission doesn't exist (or isn't ours).
      if (err instanceof Error && (err as { status?: number }).status === 404) {
        return false;
      }
      return count < 2;
    },
  });
  return {
    submission: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
